import {DataTable, DataTableProps} from "../DataTable/DataTable";
import {MeterEntity, meterType} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {DefaultText} from "../../text/DefaultText/DefaultText"
import {Anchor, Box, Group, Indicator, Modal, ModalProps} from "@mantine/core";
import {Link} from "react-router-dom";
import {usePagination} from "../DataTable/usePagination";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";
import {Tooltip} from "../../tooltip/Tooltip/Tooltip";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import {useMeterDataTableFilter} from "./useMeterDataTableFilter";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {IconBoxOff} from "@tabler/icons-react";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";

const EMPTY_ARRAY: MeterEntity[] = []

export type MeterDataTableProps = EntityDataTableProps<MeterEntity>

export const MeterDataTable = (props: MeterDataTableProps) => {
  const {records: meters = EMPTY_ARRAY, recordsPerPageOptions, ...rest} = props;

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useMeterDataTableFilter({
    records: meters
  })

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'name', direction: 'asc'}
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={[
        {
          ...getColumnFilterProps(filterConfig, "name"),
          title: t("table.entity.meter.column.name", "Name"),
          accessor: "name",
          width: 100,
          sortable: true,
          render: ({id, name}) => (
            <Anchor component={Link} to={`/meters/profile?id=${id}`}>
              {name}
            </Anchor>
          ),
        },
        {
          ...getColumnFilterProps(filterConfig, "type"),
          title: t("table.entity.meter.column.type", "Type"),
          accessor: "type",
          width: 100,
          sortable: true,
          render: ({type, flowMeterType}) => {
            const showIndicator = type === meterType.flow && typeof flowMeterType !== 'undefined'
            if (showIndicator) {
              return (
                <Tooltip label={<FormattedDatabaseCode value={flowMeterType}/>}>
                  <Indicator inline size={6} color="orange">
                    {t(type.displayValue.id, type.displayValue.defaultMessage)}
                  </Indicator>
                </Tooltip>
              )
            } else {
              return t(type.displayValue.id, type.displayValue.defaultMessage)
            }
          }
        },
        {
          ...getColumnFilterProps(filterConfig, "freightType"),
          title: t("table.entity.meter.column.freighttype", "Freight Type"),
          accessor: "freightTypeName",
          width: 100,
          sortable: true,
          render: ({freightTypeId, freightTypeName}) => (
            freightTypeId ? <DefaultText value={freightTypeName}/> : undefined
          )
        },
        {
          ...getColumnFilterProps(filterConfig, "reading"),
          title: t("table.entity.meter.column.object", "Reading"),
          accessor: "objectName",
          width: 100,
          sortable: true,
          render: ({objectId, objectType, objectName}) => {
            if (objectId) {
              return (
                <EntityAnchor
                  value={{
                    objectId: objectId,
                    objectName: objectName,
                    objectType: objectType,
                  }}
                />
              )
            } else {
              return undefined
            }
          }
        },
        {
          title: '',
          accessor: "status",
          width: 50,
          textAlignment: 'right',
          render: (meter) => (
            <Group spacing={4} position="right" noWrap>
              {meter.doNotUse && (
                <IconBoxOff size={16}/>
              )}
            </Group>
          )
        }
      ]}
    />
  );
};

export type MeterDataTableModalProps = Omit<ModalProps, "onSubmit"> & {
  dataTable: MeterDataTableProps
}

export const MeterDataTableModal = (props: MeterDataTableModalProps) => {
  const {t} = useTranslation(["common"]);

  const {
    title = t<string>("common.entity.meter.plural", "Meters"),
    dataTable,
    ...rest
  } = props;

  const {
    records,
    ...dataTableRest
  } = dataTable

  return (
    <Modal {...rest} title={title}>
      <Box>
        <MeterDataTable
          {...dataTableRest}
          recordsPerPageOptions={[5]}/>
      </Box>
    </Modal>
  );
};
